/**
 * Metadados de campo: rótulo, tipo e — o que mais importa — SEÇÃO.
 *
 * As seções são a decisão de produto virada interface. O URÂNIA tem 85 telas
 * de cadastro porque cada pedido de cliente virou uma tela; "importância das
 * janelas", "jornadas ideais", "preferência de sequência" e "preferência de
 * horários" moram em quatro delas, sendo os quatro atributos do MESMO
 * professor.
 *
 * Aqui são quatro campos de uma seção chamada "Forma da jornada", numa tela
 * só. A regra que sustenta isso, e que não pode ser afrouxada sem virar as
 * 179 telas de novo: **agrupar por entidade do domínio, nunca por restrição
 * individual.**
 */

export type TipoCampo =
  | 'texto'
  | 'numero'
  | 'hora'
  | 'escolha'
  | 'booleano'
  | 'cor'
  | 'referencia'
  /** Lista de dias da semana. Vazio é "qualquer dia", não "nenhum". */
  | 'dias'
  /** Dinheiro. Duas casas, e o campo mostra a moeda. */
  | 'moeda'
  /**
   * Só leitura: o valor vem de um campo de OUTRO registro, escolhido neste.
   * A hora-aula do professor é a da classificação dele — o formulário mostra,
   * e muda junto quando a classificação muda, mas não deixa digitar.
   */
  | 'derivado'

export interface Campo {
  nome: string
  rotulo: string
  tipo: TipoCampo
  secao: string
  /** Explicação curta. Quem volta depois de um ano é o usuário padrão. */
  ajuda?: string
  opcoes?: readonly { valor: string; rotulo: string }[]
  /** Para `referencia`: o recurso de onde vêm as opções. */
  referencia?: string
  /** Dado pessoal (LGPD). A interface marca; o schema já marcava. */
  pessoal?: boolean
  /**
   * Para `derivado`: qual campo deste registro aponta o outro (`de`), qual
   * campo do outro é mostrado (`valor`) e como (`formato`). As linhas do
   * outro vêm de `referencia`.
   */
  derivado?: { de: string; valor: string; formato: 'moeda' }
}

/**
 * A que recurso uma coluna `*_id` aponta.
 *
 * **Por que isto precisa existir.** Até aqui toda chave estrangeira era campo
 * de texto, e cadastrar uma turma significava colar o UUID do curso. Ninguém
 * monta horário assim — e o manual, ao tentar explicar a tela, foi o que
 * tornou isso impossível de ignorar.
 *
 * A convenção cobre quase tudo: `curso_id` → `cursos`. Os apelidos — a mesma
 * entidade sob outro nome de coluna — ficam listados, porque adivinhá-los
 * seria pior que escrevê-los.
 */
const APELIDOS: Record<string, string> = {
  local_padrao_id: 'locais',
  origem_unidade_id: 'unidades',
  destino_unidade_id: 'unidades',
  turno_origem_id: 'turnos',
  turno_destino_id: 'turnos',
  preset_ideal_id: 'presets',
  preset_aceitavel_id: 'presets',
}

/**
 * Plural do português que o catálogo usa.
 *
 * O `+s` dá conta de `turma`, `curso`, `oferta` e da maioria — mas não de
 * `professor` nem de `local`, e "professors" é o tipo de erro que só aparece
 * quando alguém abre a tela. `test/campos.test.ts` confere que todo plural
 * daqui cai num recurso que existe.
 */
const PLURAL: Record<string, string> = {
  local: 'locais',
  professor: 'professores',
  // `classificacaos` — a terceira vez que o plural em português quebra a
  // regra do `s`, e a terceira vez que o teste pega antes da tela.
  classificacao: 'classificacoes',
}

export function referenciaDe(nome: string): string | undefined {
  if (APELIDOS[nome]) return APELIDOS[nome]
  if (!nome.endsWith('_id')) return undefined
  const entidade = nome.slice(0, -3)
  // `alvo_id` é polimórfico — aponta para quatro tabelas conforme o
  // `alvo_tipo` ao lado. Um seletor único mentiria sobre isso.
  if (entidade === 'alvo') return undefined
  return PLURAL[entidade] ?? `${entidade}s`
}

const DIAS = [
  { valor: '1', rotulo: 'Segunda' },
  { valor: '2', rotulo: 'Terça' },
  { valor: '3', rotulo: 'Quarta' },
  { valor: '4', rotulo: 'Quinta' },
  { valor: '5', rotulo: 'Sexta' },
  { valor: '6', rotulo: 'Sábado' },
  { valor: '7', rotulo: 'Domingo' },
] as const

const SEQUENCIA = [
  { valor: 'INDIFERENTE', rotulo: 'Tanto faz' },
  { valor: 'AGRUPAR', rotulo: 'Agrupar turmas do mesmo ano' },
  { valor: 'ESPALHAR', rotulo: 'Alternar entre anos' },
] as const

const VELOCIDADE = [
  { valor: 'RAPIDO', rotulo: 'Rápido (carro)' },
  { valor: 'MEDIO', rotulo: 'Médio (ônibus)' },
  { valor: 'LENTO', rotulo: 'Lento (a pé)' },
] as const

const TIPO_PEDAGOGICO = [
  { valor: 'EXATAS', rotulo: 'Exatas' },
  { valor: 'HUMANAS', rotulo: 'Humanas' },
  { valor: 'LINGUAGENS', rotulo: 'Linguagens' },
  { valor: 'PRATICA', rotulo: 'Prática' },
  { valor: 'OUTRO', rotulo: 'Outro' },
] as const

const TIPO_ENSINO = [
  { valor: 'INFANTIL', rotulo: 'Infantil' },
  { valor: 'FUNDAMENTAL_I', rotulo: 'Fundamental I' },
  { valor: 'FUNDAMENTAL_II', rotulo: 'Fundamental II' },
  { valor: 'MEDIO', rotulo: 'Médio' },
  { valor: 'MEDIO_TECNICO', rotulo: 'Médio técnico' },
  { valor: 'TECNICO', rotulo: 'Técnico' },
  { valor: 'SUPERIOR', rotulo: 'Superior' },
  { valor: 'EJA', rotulo: 'EJA' },
] as const

/** A tela que prova o princípio: dezessete campos, quatro seções, uma tela. */
const PROFESSOR: Campo[] = [
  { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
  { nome: 'abreviatura', rotulo: 'Abreviatura', tipo: 'texto', secao: 'Identificação' },
  { nome: 'email', rotulo: 'E-mail', tipo: 'texto', secao: 'Identificação', pessoal: true },
  { nome: 'cpf', rotulo: 'CPF', tipo: 'texto', secao: 'Identificação', pessoal: true },
  { nome: 'telefone', rotulo: 'Telefone', tipo: 'texto', secao: 'Identificação', pessoal: true },
  {
    nome: 'codigo_externo',
    rotulo: 'Código externo',
    tipo: 'texto',
    secao: 'Identificação',
    ajuda: 'O código no sistema de origem. Não precisa ser único.',
  },

  {
    nome: 'min_aulas_diarias',
    rotulo: 'Mínimo de aulas por dia',
    tipo: 'numero',
    secao: 'Carga',
    ajuda: 'Evita que ele venha à escola para uma aula só.',
  },
  { nome: 'max_aulas_diarias', rotulo: 'Máximo de aulas por dia', tipo: 'numero', secao: 'Carga' },
  {
    nome: 'max_aulas_consecutivas',
    rotulo: 'Máximo de aulas seguidas',
    tipo: 'numero',
    secao: 'Carga',
  },
  { nome: 'max_minutos_diarios', rotulo: 'Máximo de minutos por dia', tipo: 'numero', secao: 'Carga' },
  {
    nome: 'max_minutos_semanais',
    rotulo: 'Máximo de minutos na semana',
    tipo: 'numero',
    secao: 'Carga',
  },
  { nome: 'max_dias_semana', rotulo: 'Máximo de dias na semana', tipo: 'numero', secao: 'Carga' },

  {
    nome: 'importancia_janelas',
    rotulo: 'Quanto incomoda ter janela',
    tipo: 'numero',
    secao: 'Forma da jornada',
    ajuda: '0 = tanto faz · 3 = janela é inaceitável.',
  },
  {
    nome: 'preferencia_sequencia',
    rotulo: 'Sequência de aulas',
    tipo: 'escolha',
    secao: 'Forma da jornada',
    opcoes: SEQUENCIA,
    ajuda: 'Repetir o mesmo plano de aula em sequência, ou variar entre anos.',
  },

  {
    nome: 'regime_id',
    rotulo: 'Regime de trabalho',
    tipo: 'referencia',
    secao: 'Carga',
    ajuda: 'De onde vêm o teto de horas-aula e o peso da janela desta pessoa.',
  },
  {
    nome: 'classificacao_id',
    rotulo: 'Classificação',
    tipo: 'referencia',
    secao: 'Carga',
    ajuda: 'O nível de carreira. É dele que sai o preço da hora-aula desta pessoa.',
  },
  {
    nome: 'valor_hora_aula',
    rotulo: 'Hora-aula desta pessoa',
    tipo: 'derivado',
    referencia: 'classificacoes',
    derivado: { de: 'classificacao_id', valor: 'valor_hora_aula', formato: 'moeda' },
    secao: 'Carga',
    ajuda:
      'Vem da classificação e não se edita aqui. Mudar o valor do nível, em Classificações e hora-aula, muda o de todos daquele nível — e o custo junto.',
  },
  {
    nome: 'max_aulas_semanais',
    rotulo: 'Máximo de aulas na semana',
    tipo: 'numero',
    secao: 'Carga',
    ajuda: 'Vazio herda do regime. Preencher aqui só quando o acordo é outro.',
  },
  {
    nome: 'jornadas_parciais_ideais',
    rotulo: 'Idas à escola por semana',
    tipo: 'numero',
    secao: 'Forma da jornada',
    ajuda: 'Quantas vezes por semana ele considera ideal vir. Vir mais ou menos que isso custa.',
  },
  {
    nome: 'importancia_jornadas_parciais',
    rotulo: 'Quanto isso importa',
    tipo: 'numero',
    secao: 'Forma da jornada',
    ajuda: '0 = irrelevante · 3 = importantíssimo.',
  },

  {
    nome: 'velocidade_deslocamento',
    rotulo: 'Como se desloca',
    tipo: 'escolha',
    secao: 'Deslocamento',
    opcoes: VELOCIDADE,
    ajuda: 'Escolhe qual dos três tempos de cada trajeto vale para ele.',
  },
  {
    nome: 'max_deslocamentos_dia',
    rotulo: 'Máximo de trajetos por dia',
    tipo: 'numero',
    secao: 'Deslocamento',
  },
  {
    nome: 'max_deslocamentos_turno',
    rotulo: 'Máximo de trajetos por turno',
    tipo: 'numero',
    secao: 'Deslocamento',
  },
]

/**
 * A hora-atividade merece o mesmo tratamento que o professor teve.
 *
 * Sem rótulo em português, `percentual_da_carga` vira "Percentual da carga" e
 * `conta_nos_limites` vira uma caixa de marcar que ninguém sabe o que faz. E
 * ela é exigência legal: a tela não pode deixar dúvida sobre o que está sendo
 * declarado, porque quem preenche errado descobre numa fiscalização.
 */
const HORA_ATIVIDADE: Campo[] = [
  { nome: 'professor_id', rotulo: 'Professor', tipo: 'referencia', secao: 'De quem' },
  {
    nome: 'tipo',
    rotulo: 'Tipo',
    tipo: 'escolha',
    secao: 'De quem',
    opcoes: [
      { valor: 'FIXA', rotulo: 'Fixa — dia e hora já decididos' },
      { valor: 'VARIAVEL', rotulo: 'Variável — o sistema escolhe onde' },
    ],
  },
  { nome: 'quantidade_semanal', rotulo: 'Quantas por semana', tipo: 'numero', secao: 'Quanto' },
  {
    nome: 'duracao_min',
    rotulo: 'Duração (minutos)',
    tipo: 'numero',
    secao: 'Quanto',
    ajuda: 'Em branco calcula a partir da carga do professor.',
  },
  {
    nome: 'percentual_da_carga',
    rotulo: 'Ou uma fração da carga',
    tipo: 'texto',
    secao: 'Quanto',
    ajuda: 'Usado quando a duração fica em branco. Ex.: 0.33 para um terço da jornada.',
  },
  {
    nome: 'conta_nos_limites',
    rotulo: 'Conta no limite diário do professor',
    tipo: 'booleano',
    secao: 'Quanto',
    ajuda: 'Em algumas redes ela ocupa uma das aulas do teto; em outras, não.',
  },
  {
    nome: 'posicao_preferida',
    rotulo: 'Onde ele prefere',
    tipo: 'escolha',
    secao: 'Onde',
    opcoes: [
      { valor: 'INDIFERENTE', rotulo: 'Tanto faz' },
      { valor: 'PONTAS', rotulo: 'Antes da primeira ou depois da última aula' },
      { valor: 'MEIO', rotulo: 'No meio do turno' },
    ],
  },
  {
    nome: 'dia',
    rotulo: 'Dia',
    tipo: 'escolha',
    secao: 'Onde',
    opcoes: DIAS,
    ajuda: 'Só para hora-atividade fixa.',
  },
  { nome: 'inicio', rotulo: 'Começa às', tipo: 'hora', secao: 'Onde', ajuda: 'Só para fixa.' },
  { nome: 'unidade_id', rotulo: 'Unidade', tipo: 'referencia', secao: 'Onde' },
  { nome: 'local_id', rotulo: 'Local', tipo: 'referencia', secao: 'Onde' },
]

const CAMPOS: Record<string, Campo[]> = {
  professores: PROFESSOR,

  // A lista técnica de Disponibilidade. Antes: dia como número, horário em
  // minutos desde a meia-noite ("450"), grau em maiúsculas do banco.
  preferencias: [
    {
      nome: 'alvo_tipo',
      rotulo: 'De quem',
      tipo: 'escolha',
      secao: 'A regra',
      opcoes: [
        { valor: 'PROFESSOR', rotulo: 'Professor' },
        { valor: 'TURMA', rotulo: 'Turma' },
        { valor: 'LOCAL', rotulo: 'Sala ou local' },
        { valor: 'OFERTA', rotulo: 'Linha da grade curricular' },
      ],
    },
    { nome: 'alvo_id', rotulo: 'Quem', tipo: 'texto', secao: 'A regra' },
    {
      nome: 'dia',
      rotulo: 'Dia',
      tipo: 'escolha',
      secao: 'A regra',
      opcoes: [
        { valor: '1', rotulo: 'Segunda' },
        { valor: '2', rotulo: 'Terça' },
        { valor: '3', rotulo: 'Quarta' },
        { valor: '4', rotulo: 'Quinta' },
        { valor: '5', rotulo: 'Sexta' },
        { valor: '6', rotulo: 'Sábado' },
        { valor: '7', rotulo: 'Domingo' },
      ],
    },
    { nome: 'inicio', rotulo: 'Das', tipo: 'hora', secao: 'A regra' },
    { nome: 'fim', rotulo: 'Até', tipo: 'hora', secao: 'A regra' },
    {
      nome: 'grau',
      rotulo: 'Grau',
      tipo: 'escolha',
      secao: 'A regra',
      ajuda: '"Não pode" proíbe. Os outros são preferência: o motor tenta respeitar, mas pode passar por cima.',
      opcoes: [
        { valor: 'IMPOSSIVEL', rotulo: 'Não pode' },
        { valor: 'MUITO_RUIM', rotulo: 'Prefere muito não' },
        { valor: 'RUIM', rotulo: 'Prefere não' },
        { valor: 'POUCO_RUIM', rotulo: 'Prefere um pouco não' },
        { valor: 'NEUTRO', rotulo: 'Tanto faz' },
        { valor: 'POUCO_BOM', rotulo: 'Prefere um pouco' },
        { valor: 'BOM', rotulo: 'Prefere' },
        { valor: 'MUITO_BOM', rotulo: 'Prefere muito' },
      ],
    },
  ],

  // D1 — de onde sai o custo da grade. O valor mora no nível e não em cada
  // cadastro: o reajuste anual é uma edição por degrau, não trezentas.
  classificacoes: [
    { nome: 'nome', rotulo: 'Nível', tipo: 'texto', secao: 'O que é' },
    {
      nome: 'valor_hora_aula',
      rotulo: 'Valor da hora-aula',
      tipo: 'moeda',
      secao: 'O que é',
      ajuda:
        'O preço de UMA hora-aula neste nível. Na ESPM a hora-aula é o crédito de 50 min, e o encontro de 1h40 vale duas.',
    },
    {
      nome: 'ordem',
      rotulo: 'Posição na carreira',
      tipo: 'numero',
      secao: 'O que é',
      ajuda:
        'Do mais júnior ao mais sênior. Em ordem alfabética, "Adjunto" viria antes de "Assistente".',
    },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'O que é' },
  ],

  regimes: [
    { nome: 'nome', rotulo: 'Regime', tipo: 'texto', secao: 'O que é' },
    {
      nome: 'max_aulas_semanais',
      rotulo: 'Máximo de aulas na semana',
      tipo: 'numero',
      secao: 'Carga',
      ajuda: 'Em branco = sem teto, que é o caso do horista. Zero seria "não pode dar aula".',
    },
    {
      nome: 'aulas_adicionais',
      rotulo: 'Aulas adicionais',
      tipo: 'numero',
      secao: 'Carga',
      ajuda:
        'O "mais 5 de adicional" do RTP. Soma ao teto, e fica à parte para a escola ver as duas parcelas.',
    },
    {
      nome: 'importancia_janelas',
      rotulo: 'Evitar janela (0 a 3)',
      tipo: 'numero',
      secao: 'Janela',
      ajuda: '3 para horista, 1 para mensalista. Professor sem valor próprio herda daqui.',
    },
    {
      nome: 'janela_e_paga',
      rotulo: 'A janela entra na folha',
      tipo: 'booleano',
      secao: 'Janela',
      ajuda:
        'Marque para horista. RTI e RTP recebem fixo, e a janela deles não custa nada a mais — é por isso que ela deve sobrar para eles.',
    },
  ],
  'horas-atividade': HORA_ATIVIDADE,

  jornadas: [
    {
      nome: 'professor_id',
      rotulo: 'Professor',
      tipo: 'texto',
      secao: 'A quem se aplica',
      ajuda: 'Em branco = regra padrão do projeto, valendo para todos.',
    },
    {
      nome: 'interjornada_min_min',
      rotulo: 'Descanso entre um dia e o seguinte (minutos)',
      tipo: 'numero',
      secao: 'Descanso',
      ajuda: 'Do fim do último horário de um dia ao início do primeiro do dia seguinte.',
    },
  ],

  coincidencias: [
    {
      nome: 'nome',
      rotulo: 'Nome',
      tipo: 'texto',
      secao: 'Identificação',
      ajuda: 'Ofertas que precisam cair no mesmo horário. Cobre turma conjugada e divisão de classe.',
    },
  ],

  trilhas: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    {
      nome: 'formato',
      rotulo: 'Formato',
      tipo: 'escolha',
      secao: 'Identificação',
      opcoes: [
        { valor: 'UM_PARA_UM', rotulo: '1:1 — uma turma-trilha por turma regular' },
        { valor: 'UM_PARA_N', rotulo: '1:N — uma turma-trilha agrega várias turmas' },
      ],
    },
    {
      nome: 'max_proporcao_grade',
      rotulo: 'Fração máxima da grade',
      tipo: 'texto',
      secao: 'Limite',
      ajuda: 'Quanto da grade da turma a trilha pode ocupar. Ex.: 0.2 para um quinto.',
    },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  deslocamentos: [
    { nome: 'origem_unidade_id', rotulo: 'Sai de', tipo: 'referencia', secao: 'Trajeto' },
    { nome: 'destino_unidade_id', rotulo: 'Vai para', tipo: 'referencia', secao: 'Trajeto' },
    {
      nome: 'tempo_rapido_min',
      rotulo: 'De carro (minutos)',
      tipo: 'numero',
      secao: 'Quanto demora',
    },
    {
      nome: 'tempo_medio_min',
      rotulo: 'De ônibus (minutos)',
      tipo: 'numero',
      secao: 'Quanto demora',
    },
    { nome: 'tempo_lento_min', rotulo: 'A pé (minutos)', tipo: 'numero', secao: 'Quanto demora' },
    {
      nome: 'qualidade',
      rotulo: 'Quanto é sofrido',
      tipo: 'numero',
      secao: 'Quanto incomoda',
      ajuda: '0 = indiferente, −3 = péssimo. Atravessar a cidade não é cruzar a rua.',
    },
    {
      nome: 'max_por_dia',
      rotulo: 'Máximo de trajetos por dia',
      tipo: 'numero',
      secao: 'Quanto incomoda',
    },
    { nome: 'max_por_turno', rotulo: 'Máximo por turno', tipo: 'numero', secao: 'Quanto incomoda' },
  ],

  presets: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    { nome: 'simbolo', rotulo: 'Símbolo', tipo: 'texto', secao: 'Identificação' },
    {
      nome: 'rotulo',
      rotulo: 'O que ele faz, em uma frase',
      tipo: 'texto',
      secao: 'Identificação',
      ajuda: 'Ex.: "Uma vez na semana, duas aulas no mesmo dia, geminadas".',
    },
    { nome: 'max_aulas_dia', rotulo: 'Máximo de aulas por dia', tipo: 'numero', secao: 'Na semana' },
    { nome: 'max_dias', rotulo: 'Em no máximo quantos dias', tipo: 'numero', secao: 'Na semana' },
    {
      nome: 'tamanho_bloco',
      rotulo: 'Tamanho do bloco',
      tipo: 'numero',
      secao: 'Blocos',
      ajuda: '1 = avulsa, 2 = par, 3 = trio.',
    },
    {
      nome: 'blocos_obrigatorios',
      rotulo: 'Blocos exigidos',
      tipo: 'numero',
      secao: 'Blocos',
      ajuda: 'Quantos são obrigatórios, e não apenas tentados.',
    },
    {
      nome: 'geminar',
      rotulo: 'Geminação',
      tipo: 'escolha',
      secao: 'Blocos',
      opcoes: [
        { valor: 'PROIBIDO', rotulo: 'Separar obrigatoriamente' },
        { valor: 'TENTAR_NAO', rotulo: 'Tentar separar' },
        { valor: 'TENTAR', rotulo: 'Tentar geminar' },
        { valor: 'OBRIGATORIO', rotulo: 'Geminar obrigatoriamente' },
      ],
    },
    {
      nome: 'cortar_no_intervalo',
      rotulo: 'O bloco pode ser partido pelo recreio',
      tipo: 'booleano',
      secao: 'Blocos',
    },
    {
      nome: 'dias_consecutivos',
      rotulo: 'Dias consecutivos',
      tipo: 'escolha',
      secao: 'Espaçamento',
      opcoes: [
        { valor: 'PERMITIDO', rotulo: 'Podem ser seguidos' },
        { valor: 'PROIBIDO', rotulo: 'Não podem ser seguidos' },
        { valor: 'UM_INTERVALO_MINIMO', rotulo: 'Ao menos um dia de intervalo' },
      ],
    },
  ],

  turmas: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    {
      nome: 'serie',
      rotulo: 'Série',
      tipo: 'numero',
      secao: 'Identificação',
      ajuda: 'Campo próprio, não texto dentro do nome — é o que habilita o limite por série.',
    },
    { nome: 'curso_id', rotulo: 'Curso', tipo: 'referencia', secao: 'Identificação' },
    { nome: 'unidade_id', rotulo: 'Unidade', tipo: 'referencia', secao: 'Local' },
    { nome: 'local_padrao_id', rotulo: 'Sala padrão', tipo: 'referencia', secao: 'Local' },
    { nome: 'cor', rotulo: 'Cor', tipo: 'cor', secao: 'Aparência' },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  disciplinas: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    { nome: 'abreviatura', rotulo: 'Abreviatura', tipo: 'texto', secao: 'Identificação' },
    { nome: 'area_id', rotulo: 'Área', tipo: 'referencia', secao: 'Classificação' },
    {
      nome: 'tipo_pedagogico',
      rotulo: 'Tipo pedagógico',
      tipo: 'escolha',
      secao: 'Classificação',
      opcoes: TIPO_PEDAGOGICO,
      ajuda: 'Alimenta o critério de "muitas matérias pesadas no mesmo dia".',
    },
    { nome: 'cor', rotulo: 'Cor', tipo: 'cor', secao: 'Aparência' },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  cursos: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    { nome: 'abreviatura', rotulo: 'Abreviatura', tipo: 'texto', secao: 'Identificação' },
    {
      nome: 'tipo_ensino',
      rotulo: 'Tipo de ensino',
      tipo: 'escolha',
      secao: 'Identificação',
      opcoes: TIPO_ENSINO,
    },
    { nome: 'periodo_min', rotulo: 'Do período', tipo: 'numero', secao: 'Períodos' },
    { nome: 'periodo_max', rotulo: 'Ao período', tipo: 'numero', secao: 'Períodos' },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  unidades: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    { nome: 'abreviatura', rotulo: 'Abreviatura', tipo: 'texto', secao: 'Identificação' },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  locais: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    { nome: 'abreviatura', rotulo: 'Abreviatura', tipo: 'texto', secao: 'Identificação' },
    { nome: 'unidade_id', rotulo: 'Unidade', tipo: 'referencia', secao: 'Identificação' },
    {
      nome: 'capacidade_simultanea',
      rotulo: 'Capacidade simultânea',
      tipo: 'numero',
      secao: 'Uso',
      ajuda: '1 = sala comum. Acima disso, é recurso compartilhado.',
    },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  turnos: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    // **Hora, e não número.** O banco guarda minuto do dia e a tela mostrava
    // o número cru: a manhã aparecia como "450 às 780", com a ajuda pedindo
    // que a pessoa calculasse minutos desde a meia-noite de cabeça. O tipo
    // `hora` já existia e faz a conversão nas duas direções.
    {
      nome: 'inicio',
      rotulo: 'Começa às',
      tipo: 'hora',
      secao: 'Horário',
      ajuda: 'O primeiro minuto do turno. Na ESPM a manhã começa 07:30.',
    },
    {
      nome: 'fim',
      rotulo: 'Termina às',
      tipo: 'hora',
      secao: 'Horário',
      ajuda: 'O último. É esta faixa que decide o que é "fora do turno" para a oferta.',
    },
    { nome: 'ordem', rotulo: 'Ordem', tipo: 'numero', secao: 'Identificação' },
  ],

  areas: [
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', secao: 'Identificação' },
    {
      nome: 'max_aulas_dia',
      rotulo: 'Máximo de aulas por dia',
      tipo: 'numero',
      secao: 'Limite',
      ajuda: 'Teto do grupo inteiro numa turma, por dia. Pode ter override por turma.',
    },
    { nome: 'cor', rotulo: 'Cor', tipo: 'cor', secao: 'Aparência' },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'Identificação' },
  ],

  ofertas: [
    { nome: 'turma_id', rotulo: 'Turma', tipo: 'referencia', secao: 'O que é' },
    { nome: 'disciplina_id', rotulo: 'Disciplina', tipo: 'referencia', secao: 'O que é' },
    { nome: 'aulas_semanais', rotulo: 'Aulas por semana', tipo: 'numero', secao: 'O que é' },
    {
      nome: 'turno_id',
      rotulo: 'Turno',
      tipo: 'referencia',
      secao: 'O que é',
      ajuda:
        'Vazio = qualquer horário do esquema da turma. O turno é desta linha, e não da turma: a mesma turma costuma ter disciplina de manhã e de tarde.',
    },
    {
      nome: 'grau_fora_do_turno',
      rotulo: 'Fora do turno é',
      tipo: 'escolha',
      secao: 'O que é',
      ajuda:
        'Vazio usa a regra do projeto. Aqui se declara a exceção: o turno pode ser preferência numa disciplina e regra dura em outra, no mesmo semestre.',
      opcoes: [
        { valor: 'IMPOSSIVEL', rotulo: 'Impossível — regra dura' },
        { valor: 'MUITO_RUIM', rotulo: 'Muito ruim' },
        { valor: 'RUIM', rotulo: 'Ruim' },
        { valor: 'POUCO_RUIM', rotulo: 'Pouco ruim' },
        { valor: 'NEUTRO', rotulo: 'Neutro — turno só informa' },
      ],
    },
    {
      nome: 'dias_permitidos',
      rotulo: 'Dias em que pode cair',
      tipo: 'dias',
      secao: 'O que é',
      ajuda:
        'Vazio = qualquer dia, que é o normal. Marque só quando a coordenação determinou o dia — todo minor é segunda ou quinta, por exemplo.',
    },
    {
      nome: 'trilha_id',
      rotulo: 'Itinerário (trilha)',
      tipo: 'referencia',
      secao: 'O que é',
      ajuda:
        'Vazio para aula regular. Preenchido, esta aula ocupa TODAS as turmas de origem da trilha — e pode acontecer ao mesmo tempo que outra trilha.',
    },
    {
      nome: 'preset_ideal_id',
      rotulo: 'Forma ideal',
      tipo: 'referencia',
      secao: 'Como se distribui',
      ajuda: 'Como as aulas se espalham pela semana, no melhor caso.',
    },
    {
      nome: 'preset_aceitavel_id',
      rotulo: 'Forma aceitável',
      tipo: 'referencia',
      secao: 'Como se distribui',
      ajuda: 'A válvula de escape: o que se aceita a contragosto para o horário fechar.',
    },
    {
      nome: 'penalidade_aceitavel',
      rotulo: 'Quanto custa cair na aceitável',
      tipo: 'escolha',
      secao: 'Como se distribui',
      opcoes: [
        { valor: 'POUCO_RUIM', rotulo: 'Pouco ruim' },
        { valor: 'RUIM', rotulo: 'Ruim' },
        { valor: 'MUITO_RUIM', rotulo: 'Muito ruim' },
      ],
    },
    { nome: 'codigo_externo', rotulo: 'Código externo', tipo: 'texto', secao: 'O que é' },
  ],
}

/** Um campo sem metadado, em estado legível. */
function padrao(nome: string): Campo {
  return {
    nome,
    rotulo: nome
      .replace(/_id$/, '')
      .replaceAll('_', ' ')
      .replace(/^./, (c) => c.toUpperCase()),
    tipo: referenciaDe(nome)
      ? 'referencia'
      : nome.startsWith('max_') || nome.startsWith('min_')
        ? 'numero'
        : 'texto',
    secao: 'Campos',
    referencia: referenciaDe(nome),
  }
}

/**
 * Os campos de um recurso, **sempre a partir do catálogo**.
 *
 * **A inversão aqui é uma correção, e ela tem data.** Antes, havendo lista
 * escrita à mão, ela era devolvida INTEIRA e a lista do catálogo era
 * ignorada. O efeito: coluna nova no banco aparecia na API, na projeção e no
 * motor, e não aparecia na tela — calada, porque nada quebrava.
 *
 * Medido em 27/10, com três campos invisíveis: `professor.regime_id` e
 * `professor.max_aulas_semanais`, de onde vêm o teto de carga e o peso da
 * janela de quem dá aula; e `oferta.turno_id`, que eu tinha acrescentado na
 * mesma manhã, documentado no manual, e esquecido aqui. Quem achou os três
 * foi o tour do manual, apontando para um campo que a tela não desenhava.
 *
 * Agora o catálogo manda: ele diz QUAIS campos existem, e esta tabela diz
 * apenas COMO desenhar os que ela conhece. Campo novo nasce visível, com
 * rótulo derivado do nome, até alguém escrever metadado melhor.
 */
export function camposDe(caminho: string, nomes: readonly string[]): Campo[] {
  const conhecidos = CAMPOS[caminho]
  if (!conhecidos) return nomes.map(padrao)

  const porNome = new Map(conhecidos.map((c) => [c.nome, c]))
  // Campo derivado não é coluna — o catálogo nunca o lista. Entra logo
  // depois do campo de que depende, quando esse existe.
  const derivadosDe = new Map<string, Campo[]>()
  for (const c of conhecidos) {
    if (c.tipo !== 'derivado' || !c.derivado) continue
    derivadosDe.set(c.derivado.de, [...(derivadosDe.get(c.derivado.de) ?? []), c])
  }
  return nomes.flatMap((nome) => {
    const campo = porNome.get(nome)
    const depois = derivadosDe.get(nome) ?? []
    if (!campo) return [padrao(nome), ...depois]
    // A tabela acima diz COMO desenhar; de onde vêm as opções é convenção, e
    // repeti-la em cada linha só criaria uma segunda chance de errar.
    if (campo.tipo === 'referencia' && !campo.referencia) {
      return [{ ...campo, referencia: referenciaDe(nome) }, ...depois]
    }
    return [campo, ...depois]
  })
}

export function secoesDe(campos: Campo[]): { nome: string; campos: Campo[] }[] {
  const ordem: string[] = []
  const mapa = new Map<string, Campo[]>()
  for (const campo of campos) {
    if (!mapa.has(campo.secao)) {
      mapa.set(campo.secao, [])
      ordem.push(campo.secao)
    }
    mapa.get(campo.secao)!.push(campo)
  }
  return ordem.map((nome) => ({ nome, campos: mapa.get(nome)! }))
}
